import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, animate, useInView } from "framer-motion";
import {
  ArrowRight,
  Check,
  Search,
  TrendingUp,
  Coins,
  Users,
  Cpu,
  GitBranch,
  Linkedin,
  ShieldCheck,
  BarChart3,
  Calendar,
  ChevronDown,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation, useLang } from "@/lib/i18n";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { LINKEDIN_URL, CAL_BOOKING_URL } from "@/lib/constants";
import Navbar from "@/components/Navbar";
import FooterCTA from "@/components/FooterCTA";
import ContactForm from "@/components/ContactForm";
import BuiltForCarousel from "@/components/BuiltForCarousel";
import { AvailabilityPill } from "@/components/AvailabilityPill";

const auditIcons = [Search, TrendingUp, Coins, Users, Cpu, GitBranch];

/* ── Inline count-up metric ─────────────────────────────────────────────── */
function AnimatedMetric({ value, suffix, label, delay = 0 }: { value: string; suffix: string; label: string; delay?: number }) {
  const num = parseInt(value, 10);
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced) { setDisplay(num); return; }
    const timeout = setTimeout(() => {
      const ctrl = animate(mv, num, { duration: 1.2, ease: [0.16, 1, 0.3, 1] });
      const unsub = mv.on("change", (v) => setDisplay(Math.round(v)));
      return () => { ctrl.stop(); unsub(); };
    }, delay * 1000);
    return () => clearTimeout(timeout);
  }, [num, delay, mv, prefersReduced]);

  return (
    <div className="flex flex-col gap-1 py-4 xl:py-0 xl:px-8 xl:min-w-[180px] xl:items-start">
      <span className="sr-only">{value}{suffix} {label}</span>
      <span role="img" aria-hidden="true" className="text-5xl lg:text-6xl font-display tabular-nums text-foreground leading-none">
        {display}<span className="text-primary">{suffix}</span>
      </span>
      <span className="text-xs font-mono font-semibold tracking-[0.15em] text-muted-foreground uppercase mt-1 whitespace-pre-line">{label}</span>
    </div>
  );
}

/* ── Process step with count-up + ring glow ─────────────────────────────── */
function ProcessStep({ step, index, anim, prefersReduced }: { step: { label: string; body: string }; index: number; anim: any; prefersReduced: boolean | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [count, setCount] = useState("00");

  useEffect(() => {
    if (!inView) return;
    const target = index + 1;
    if (prefersReduced) { setCount(String(target).padStart(2, "0")); return; }
    let current = 0;
    const interval = 600 / target;
    const timer = setInterval(() => {
      current++;
      setCount(String(current).padStart(2, "0"));
      if (current >= target) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [inView, index, prefersReduced]);

  return (
    <motion.div
      ref={ref}
      initial={anim.initial}
      whileInView={anim.whileInView}
      viewport={anim.viewport}
      transition={{ delay: index * 0.12, ...anim.transition }}
      className="flex flex-col items-center text-center"
    >
      <motion.div
        className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center mb-6 flex-shrink-0 z-10 ${
          index === 0 ? "border-primary bg-primary/20" : "border-primary/40 bg-primary/10"
        }`}
        whileInView={prefersReduced ? {} : {
          boxShadow: ["0 0 0 4px hsl(166 84% 46% / 0.4)", "0 0 0 0px transparent"],
        }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ delay: index * 0.12 + 0.3, duration: 1, ease: "easeOut" }}
      >
        <span className="font-display text-lg text-primary">{count}</span>
      </motion.div>
      <p className="text-xs font-mono font-semibold tracking-widest text-primary uppercase mb-3">
        {step.label}
      </p>
      <p className="text-sm font-body text-muted-foreground leading-relaxed">{step.body}</p>
    </motion.div>
  );
}

const Web3Audit = () => {
  const t = useTranslation();
  const { lang } = useLang();
  const w = t.web3audit;
  const prefersReduced = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
  }, []);

  const canonical = lang === "pl" ? "https://chrisworkflows.com/pl/web3" : "https://chrisworkflows.com/web3";
  useDocumentMeta({
    title: w.meta.title,
    description: w.meta.description,
    canonical,
    robots: "index, follow",
    ogTitle: w.meta.title,
    ogDescription: w.meta.description,
    ogImage: `https://chrisworkflows.com/og/web3-${lang}.jpg?v=3`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Web3 GTM Consulting",
      "serviceType": "Go-to-Market Consulting",
      "description": w.meta.description,
      "url": canonical,
      "provider": { "@id": "https://chrisworkflows.com/#chris" },
      "areaServed": { "@type": "Place", "name": "Worldwide" },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Web3 founders, DeFi protocols, L1/L2 ecosystems, crypto VCs",
      },
    },
  });

  const anim = prefersReduced
    ? {
        initial: {},
        whileInView: {},
        viewport: { once: true } as const,
        transition: { duration: 0 },
      }
    : {
        initial: isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" } as const,
        transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
      };

  /* ── Cursor spotlight ref ────────────────────────────────────────────── */
  const heroRef = useRef<HTMLElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const pricingCtaRef = useRef<HTMLAnchorElement>(null);
  const pricingCtaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    const spot = spotlightRef.current;
    if (!hero || !spot || prefersReduced) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const onMove = (e: MouseEvent) => {
      const rect = hero.getBoundingClientRect();
      spot.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      spot.style.setProperty("--my", `${e.clientY - rect.top}px`);

      // Magnetic hero CTA — section-level so button responds from far away
      if (ctaRef.current) {
        const r = ctaRef.current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = (e.clientX - cx) * 0.08;
        const dy = (e.clientY - cy) * 0.08;
        const max = 8;
        ctaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
      }
    };
    const onLeave = () => {
      const rect = hero.getBoundingClientRect();
      spot.style.setProperty("--mx", `${rect.width / 2}px`);
      spot.style.setProperty("--my", `${rect.height / 2}px`);
      if (ctaRef.current) ctaRef.current.style.transform = "translate(0, 0)";
    };

    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);
    return () => { hero.removeEventListener("mousemove", onMove); hero.removeEventListener("mouseleave", onLeave); };
  }, [prefersReduced]);

  const handlePricingCtaMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !pricingCtaRef.current || !window.matchMedia("(pointer: fine)").matches) return;
    const rect = pricingCtaRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = Math.max(-8, Math.min(8, (e.clientX - cx) * 0.15));
    const dy = Math.max(-8, Math.min(8, (e.clientY - cy) * 0.15));
    pricingCtaRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
  }, [prefersReduced]);

  const handlePricingCtaLeave = useCallback(() => {
    if (pricingCtaRef.current) pricingCtaRef.current.style.transform = "translate(0, 0)";
  }, []);

  /* ── Scroll affordance fade ──────────────────────────────────────────── */
  const { scrollY } = useScroll();
  const scrollOpacity = useTransform(scrollY, [0, 60], [1, 0]);

  /* ── Word-split helper ───────────────────────────────────────────────── */
  const wordStagger = 0.06;
  const wordEase: [number, number, number, number] = [0.16, 1, 0.3, 1];

  const renderWords = (text: string, startIndex: number, className?: string) => {
    if (prefersReduced || isMobile) {
      return text.split(" ").map((word, i) => (
        <span key={startIndex + i} className={`inline-block mr-[0.1em] ${className || ""}`}>
          {word}
        </span>
      ));
    }
    return text.split(" ").map((word, i) => (
      <motion.span
        key={startIndex + i}
        initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ delay: 0.3 + (startIndex + i) * wordStagger, duration: 0.7, ease: wordEase }}
        className={`inline-block mr-[0.1em] ${className || ""}`}
      >
        {word}
      </motion.span>
    ));
  };

  const line1Words = w.hero.headingLine1.split(" ").length;
  const line2Words = w.hero.headingLine2.split(" ").length;

  /* ── Radar chart: dynamic-import on desktop only ─────────────────────── */
  const [RadarChartComp, setRadarChartComp] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    if (window.innerWidth < 640) return;
    let cancelled = false;
    import("@/components/Web3RadarChart").then((mod) => {
      if (!cancelled) setRadarChartComp(() => mod.default);
    });
    return () => { cancelled = true; };
  }, []);

  /* ── Audit card cursor tracking ──────────────────────────────────────── */
  const handleCardMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--card-mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--card-my", `${e.clientY - rect.top}px`);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* ── S1: Hero ─────────────────────────────────────────────────────────── */}
      <section ref={heroRef} className="relative flex flex-col min-h-[80vh] pt-10">
        {/* Cursor spotlight */}
        <div
          ref={spotlightRef}
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            background: "radial-gradient(600px circle at var(--mx, 50%) var(--my, 50%), hsl(var(--primary) / 0.06), transparent 100%)",
            mixBlendMode: "screen",
          }}
        />

        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(166 84% 46%) 1px, transparent 1px), linear-gradient(90deg, hsl(166 84% 46%) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[100px]" />

        <div className="flex-1 flex items-center">
          <div className="container mx-auto px-6 xl:max-w-[1400px] relative z-10 py-12">
            {/* Availability pill */}
            <div className="flex justify-end mb-6 lg:mb-0 lg:absolute lg:top-20 lg:right-6">
              <AvailabilityPill text={w.hero.availability} />
            </div>

            <div className="flex flex-col xl:flex-row xl:items-center xl:gap-16">
              {/* Left column: text (60%) */}
              <div className="xl:w-[60%]">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-8"
                >
                  <span className="w-8 h-px bg-primary animate-pulse-glow" />
                  {w.hero.eyebrow}
                </motion.p>

                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-display leading-[1.05] tracking-tight mb-8">
                  <span className="block">{renderWords(w.hero.headingLine1 + " " + w.hero.headingLine2, 0)}</span>
                  <span className="block">{renderWords(w.hero.headingHighlight, line1Words + line2Words, "text-gradient")}</span>
                  {w.hero.headingHighlight2 && (
                    <span className="block">{renderWords(w.hero.headingHighlight2, line1Words + line2Words + w.hero.headingHighlight.split(" ").length, "text-gradient")}</span>
                  )}
                </h1>

                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.5 }}
                  className="text-lg font-body text-muted-foreground leading-relaxed max-w-xl mb-4"
                >
                  {w.hero.sub}
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.55, duration: 0.5 }}
                  className="flex items-center gap-2 text-sm font-body text-muted-foreground mb-10"
                >
                  <Check size={14} className="text-primary" />
                  {w.hero.meta}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="flex flex-wrap items-center gap-4"
                >
                  <div>
                    <a
                      ref={ctaRef}
                      href="#book"
                      className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all glow"
                      style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
                    >
                      {w.hero.ctaPrimary}
                      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  </div>
                  <a
                    href="#audit"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-body font-medium text-foreground border border-border hover:border-primary/40 transition-colors"
                  >
                    {w.hero.ctaSecondary}
                  </a>
                </motion.div>
              </div>

              {/* Right column: typographic metrics */}
              <div className="xl:w-[40%] mt-12 xl:mt-0 relative">
                {/* Rotating conic gradient */}
                <div
                  className="absolute inset-0 -m-8 pointer-events-none hidden xl:block opacity-[0.06]"
                  style={{
                    background: "conic-gradient(from 0deg, hsl(var(--primary)), transparent, hsl(var(--accent)))",
                    animation: "spin 30s linear infinite",
                    borderRadius: "50%",
                    filter: "blur(80px)",
                  }}
                />

                <div className="flex flex-col divide-y divide-border/50 xl:flex-row xl:divide-y-0 xl:divide-x xl:divide-border/50 relative z-10">
                  {w.hero.statsPills.map((pill, i) => (
                    <AnimatedMetric
                      key={i}
                      value={pill.value}
                      suffix={pill.suffix}
                      label={pill.label}
                      delay={0.7 + i * 0.15}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Built For carousel inside hero ──────────────────────────────────── */}
        <div className="container mx-auto px-6 mt-12 mb-8 relative z-10">
          <BuiltForCarousel
            eyebrow={w.builtFor.eyebrow}
            items={w.builtFor.items}
          />
        </div>

        {/* Scroll affordance */}
        <motion.div
          style={{ opacity: scrollOpacity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none z-10"
        >
          <span className="text-[11px] font-mono text-muted-foreground tracking-widest">{t.scrollLabel}</span>
          <ChevronDown size={14} className="text-muted-foreground animate-bounce" />
        </motion.div>
      </section>


      {/* ── Who this is for (standalone section) ─────────────────────────────── */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="text-center mb-10"
          >
            <h2 className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {w.whoFor.eyebrow}
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {w.whoFor.items.map((item, i) => (
              <motion.div
                key={i}
                initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="flex items-start gap-4"
              >
                <span className="text-3xl sm:text-4xl font-display text-primary/30 leading-none flex-shrink-0 select-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-xl sm:text-2xl font-display text-foreground">{item.title}</h3>
                  <p className="text-sm font-body text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── S2: What the audit covers ─────────────────────────────────────────── */}
      <section id="audit" className="py-8 sm:py-16 bg-card/20 border-y border-border/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="text-center mb-8"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {w.whatIAudit.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {w.whatIAudit.headingPrefix}<span className="text-gradient italic pr-2">{w.whatIAudit.headingGradient}</span>{w.whatIAudit.headingSuffix}
            </h2>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-2 sm:gap-6 lg:gap-12 items-center">
            {/* Left: Radar Chart (desktop only — mobile skips chunk download) */}
            {RadarChartComp && (
              <div className="w-full lg:w-2/5 flex justify-center -my-16 sm:my-0">
                <div className="relative w-[100vw] h-[100vw] max-w-[500px] max-h-[500px] sm:w-[500px] sm:h-[500px] lg:w-[600px] lg:h-[600px]">
                  <RadarChartComp />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span
                      className="text-4xl font-display text-primary/30"
                      style={!prefersReduced ? {
                        animation: "pulse-badge 3s ease-in-out infinite",
                      } : undefined}
                    >
                      06
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Right: 2x3 card grid */}
            <div className="w-full lg:w-3/5 grid sm:grid-cols-2 gap-4">
              {w.whatIAudit.items.map((item, i) => {
                const Icon = auditIcons[i];
                return (
                  <motion.div
                    key={i}
                    initial={anim.initial}
                    whileInView={anim.whileInView}
                    viewport={anim.viewport}
                    transition={{ delay: i * 0.06, ...anim.transition }}
                    onMouseMove={handleCardMove}
                    className="relative rounded-xl border border-border bg-card/60 p-5 card-hover group overflow-hidden hover:border-primary/50 transition-colors"
                    style={{
                      background: "radial-gradient(600px circle at var(--card-mx, 50%) var(--card-my, 50%), hsl(var(--primary) / 0.06), transparent 70%), hsl(var(--card) / 0.6)",
                    }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors flex-shrink-0">
                        <Icon size={16} />
                      </div>
                      <h3 className="text-lg sm:text-xl font-display text-foreground">{item.title}</h3>
                    </div>
                    <p className="text-xs font-body text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>


      {/* ── S4: Hard Deliverables ─────────────────────────────────────────────── */}
      <section className="py-16 bg-card/40 border-y border-border/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="text-center mb-14"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3">
              {w.whatYouGet.sub}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {w.whatYouGet.heading}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {w.whatYouGet.items.map((item, i) => (
              <motion.div
                key={i}
                initial={anim.initial}
                whileInView={anim.whileInView}
                viewport={anim.viewport}
                transition={{ delay: i * 0.08, ...anim.transition }}
                className="relative rounded-xl border border-border bg-card p-6 card-hover group overflow-hidden"
              >
                <div className="absolute top-0 left-0 bottom-0 w-1 bg-primary/30 group-hover:bg-primary/80 group-hover:w-1.5 transition-all duration-300 rounded-l-xl" />
                <h3 className="text-lg font-display text-foreground mb-2 pl-3">{item.title}</h3>
                <p className="text-sm font-body text-muted-foreground leading-relaxed pl-3">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── S5: The Process ───────────────────────────────────────────────────── */}
      <section className="py-8 sm:py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="text-center mb-8 sm:mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">{w.process.heading}</h2>
          </motion.div>

          {/* Horizontal timeline */}
          <div className="relative max-w-5xl mx-auto">
            {/* Connecting line segments */}
            <div className="hidden lg:flex absolute top-7 left-[calc(12.5%)] right-[calc(12.5%)] h-px">
              {[0, 1, 2].map((seg) => (
                <motion.div
                  key={seg}
                  className="flex-1 h-px bg-border"
                  initial={prefersReduced ? {} : { scaleX: 0 }}
                  whileInView={prefersReduced ? {} : { scaleX: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: seg * 0.15, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  style={{ transformOrigin: "left" }}
                />
              ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-6">
              {w.process.steps.map((step, i) => (
                <ProcessStep key={i} step={step} index={i} anim={anim} prefersReduced={prefersReduced} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── S6: Why work with me ──────────────────────────────────────────────── */}
      <section className="py-16 bg-card/40 border-y border-border/40">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start max-w-5xl mx-auto">
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={anim.transition}
            >
              <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
                {w.whyMe.eyebrow}
              </p>
              <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-8">
                {w.whyMe.heading}
              </h2>

              <div className="space-y-5 mb-8">
                {w.whyMe.credibility.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={anim.initial}
                    whileInView={anim.whileInView}
                    viewport={anim.viewport}
                    transition={{ delay: i * 0.08, ...anim.transition }}
                    className="border-l-[3px] border-primary/40 pl-5"
                  >
                    <h3 className="text-base font-display text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm font-body text-muted-foreground leading-relaxed">{item.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="relative p-5 rounded-xl border border-primary/20 bg-primary/5">
                <span className="absolute -top-2 -left-1 font-display text-6xl text-primary/20 leading-none select-none" style={{ fontFamily: "'Instrument Serif', serif" }}>"</span>
                <p className="text-base font-display text-foreground italic">
                  {w.whyMe.closer.split('\n').map((line, i) => (
                    <span key={i}>
                      {i > 0 && (
                        <>
                          <span className="lg:hidden"> </span>
                          <br className="hidden lg:block" />
                        </>
                      )}
                      {line}
                    </span>
                  ))}
                </p>
              </div>
            </motion.div>

            {/* Photo + caption (AITeams style) */}
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.2, ...anim.transition }}
              className="flex flex-col items-center lg:items-end"
            >
              <div className="relative w-full max-w-[340px]">
                <div
                  className="absolute -inset-4 rounded-2xl bg-primary/10 blur-2xl -z-10"
                  style={!prefersReduced ? { animation: "glow-breathe 6s ease-in-out infinite" } : undefined}
                />
                <picture>
                  <source srcSet="/images/chris.webp" type="image/webp" />
                  <img
                    src="/images/chris.jpg"
                    alt={w.whyMe.photoName}
                    className="w-full rounded-2xl border border-primary/15 object-cover"
                    style={{ boxShadow: "0 0 40px -10px rgba(19, 216, 170, 0.2)" }}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="mt-4 text-center lg:text-right">
                <p className="font-body font-semibold text-foreground text-sm">{w.whyMe.photoName}</p>
                <p className="text-xs font-mono text-muted-foreground tracking-wide">{w.whyMe.photoRole}</p>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-body font-medium text-muted-foreground hover:text-primary transition-colors mt-2"
                >
                  <Linkedin size={14} />
                  {w.whyMe.linkedinVerifyLabel}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── S8: Pricing + Booking ─────────────────────────────────────────────── */}
      <section id="scope" className="py-16 bg-card/20 border-y border-border/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="text-center mb-14"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {w.scope.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {w.scope.heading}
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 max-w-5xl mx-auto items-stretch">
            {/* Left: Pricing table */}
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.1, ...anim.transition }}
              className="lg:h-full"
            >
              <div className="rounded-xl border border-border bg-card overflow-hidden lg:h-full flex flex-col">
                <div className="flex-1">
                  {w.scope.rows.map((row, i) => {
                    const isLast = i === w.scope.rows.length - 1;
                    return (
                      <div
                        key={i}
                        className={`px-6 py-4 hover:bg-primary/5 transition-colors ${
                          isLast
                            ? "flex flex-col items-center gap-1 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:text-left border-t-2 border-border"
                            : "flex items-center justify-between " + (i < w.scope.rows.length - 1 ? "border-b border-border/40" : "")
                        }`}
                      >
                        <span className="text-sm font-body text-foreground">{row.label}</span>
                        <span className={`font-display tabular-nums ${
                          isLast ? "text-base sm:text-xl text-gradient-premium" : "text-lg text-foreground"
                        }`}>
                          {row.value}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Footnote inside card bottom */}
                <p className="text-xs text-muted-foreground px-6 py-3 border-t border-border/40">{w.scope.footnote}</p>
              </div>
            </motion.div>

            {/* Right: Booking card */}
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.2, ...anim.transition }}
              className="lg:sticky lg:top-24"
            >
              <div id="book" className="rounded-2xl border border-border bg-card p-8 scroll-mt-[40vh] lg:scroll-mt-[180px] lg:h-full">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={16} className="text-primary" />
                  <h3 className="text-xl font-display text-foreground">{w.scope.bookCardTitle}</h3>
                </div>
                <p className="text-sm font-body text-muted-foreground leading-relaxed mb-6">
                  {w.scope.bookCardDesc}
                </p>
                <div
                  ref={pricingCtaContainerRef}
                  onMouseMove={handlePricingCtaMove}
                  onMouseLeave={handlePricingCtaLeave}
                  className="mb-6"
                >
                  <a
                    ref={pricingCtaRef}
                    href={`${CAL_BOOKING_URL}?utm_source=web3-page&utm_medium=website&utm_campaign=pricing-cta`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 w-full px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all glow"
                    style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
                  >
                    {w.scope.bookCardCta}
                    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  {w.scope.fallback}{" "}
                  <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {w.scope.fallbackLink}
                  </a>
                </p>
              </div>
            </motion.div>
          </div>

          <div className="mt-12">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* ── S9: FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 max-w-5xl mx-auto">
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={anim.transition}
              className="lg:w-2/5"
            >
              <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-4">
                {w.faq.heading}
              </h2>
              <div className="lg:sticky lg:top-32 p-6 rounded-xl border border-primary/20 bg-card">
                <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-2">
                  <span
                    className="inline-block mr-1.5"
                    style={!prefersReduced ? {
                      animation: "pulse-dot 2s ease-in-out infinite",
                    } : undefined}
                  >●</span>
                  {w.faq.panelEyebrow}
                </p>
                <p className="text-base font-display text-foreground mb-1">{w.faq.panelHeading}</p>
                <p className="text-sm font-body text-muted-foreground mb-4">{w.faq.panelBody}</p>
                <a
                  href="#scope"
                  className="inline-flex items-center gap-2 text-sm font-body font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  {w.faq.panelCta}
                  <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>

            <div className="lg:w-3/5 lg:mt-[40px]">
              <Accordion type="single" collapsible className="w-full">
                {w.faq.items.map((item, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-border/40 transition-all">
                    <AccordionTrigger className="text-left font-display text-foreground text-base py-5 hover:text-primary transition-colors">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm font-body text-muted-foreground leading-relaxed pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>




      <FooterCTA variant="web3audit" />
    </div>
  );
};

export default Web3Audit;
